// src/_components/HeaderDesktop.jsx
import React, { useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import NotifacitonPanel from "./NotificationPanel";


// 作用域 id，防止样式被外部覆盖
function useScopeId(prefix = "hdr") {
  return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 8)}`, [prefix]);
}

/**
 * HeaderDesktop（Underline Style）
 * props:
 *  - active: 'home' | 'planner' | 'community' | 'messages'
 *  - cityName: string
 *  - unread: number
 *  - onSearch: (q: string) => void
 *  - maxWidth: string | number  内容最大宽度（默认 1280px）
 */
export default function HeaderDesktop({
  active = "home",
  cityName = "Chengdu",
  unread = 3,
  onSearch,
  maxWidth = 1280,
}) {
  const scope = useScopeId();
  const nav = useNavigate();
  const maxW = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;
  const [openNtf, setOpenNtf] = React.useState(false);
  const items = [
    {
      id: "1",
      type: "itinerary",
      title: "Itinerary Reminder",
      desc: "Your 3-day Montreal trip starts tomorrow. Don’t forget your camera!",
      time: "1h ago",
      unread: true,
      actionText: "View itinerary",
      onAction: () => nav("/get-itinerary"),
    },
    {
      id: "2",
      type: "booking",
      title: "Booking Confirmed",
      desc: "Admission to Notre-Dame Basilica is confirmed. Check your email for the QR code.",
      time: "2h ago",
      unread: true,
      actionText: "View booking",
      onAction: () => nav("/orders"),
    },
    {
      id: "3",
      type: "community",
      title: "Community Comment",
      desc: "Someone commented on your post “Best Poutine in Montreal.”",
      time: "4h ago",
      unread: false,
      actionText: "Open thread",
      onAction: () => nav("/community"),
    },
    {
      id: "4",
      type: "weather",
      title: "Weather Heads-up",
      desc: "Light snow flurries expected in Montreal tomorrow. Dress warm and wear boots.",
      time: "6h ago",
      unread: false,
    },
    {
      id: "5",
      type: "reward",
      title: "Points Awarded",
      desc: "Congrats! You unlocked “Old Montreal Explorer” and earned 150 points.",
      time: "Yesterday",
      unread: false,
    },
    {
      id: "6",
      type: "event",
      title: "Festival Reminder",
      desc: "Montreal Jazz Festival starts this weekend. Street stages open at 6:00 PM.",
      time: "2d ago",
      unread: false,
      actionText: "See events",
      onAction: () => nav("/events"),
    },
    {
      id: "7",
      type: "booking",
      title: "Restaurant Reminder",
      desc: "Your table at Schwartz’s Deli is at 12:30 PM tomorrow.",
      time: "2d ago",
      unread: false,
      actionText: "View details",
      onAction: () => nav("/reservations"),
    },
    {
      id: "8",
      type: "itinerary",
      title: "Day Plan Updated",
      desc: "We reordered today’s route around the Plateau due to construction on St-Laurent.",
      time: "3d ago",
      unread: false,
      actionText: "Review plan",
      onAction: () => nav("/get-itinerary"),
    },
    {
      id: "9",
      type: "transport",
      title: "STM Service Change",
      desc: "Metro Line 2 (Orange) has weekend maintenance. Expect 10–15 min delays.",
      time: "3d ago",
      unread: false,
      actionText: "See advisory",
      onAction: () => nav("/transport"),
    },
    {
      id: "10",
      type: "booking",
      title: "Hotel Check-in",
      desc: "Hotel Nelligan mobile check-in is available from 3:00 PM today.",
      time: "Today",
      unread: false,
      actionText: "Check in",
      onAction: () => nav("/stays"),
    },
  ];


  const css = `
[data-hdr="${scope}"] .bar{
  position:sticky; top:0; z-index:50;
  height:64px; background:#fff;
  border-bottom:1px solid #eef1f4;
}
[data-hdr="${scope}"] .inner{
  width:100%; max-width:${maxW}; margin:0 auto;
  height:64px;
  display:flex; align-items:center;
  gap:16px; padding:0 16px;
  flex-wrap: nowrap;      /* 保持一行 */
  overflow: visible;      /* ✅ 别裁剪，消息菜单被挤时不会被直接切掉 */
  min-width: 0;           /* ✅ 允许子元素收缩 */
}

  /* 品牌 */
  [data-hdr="${scope}"] .brand{display:flex; align-items:center; gap:10px; margin-right:4px;}
  [data-hdr="${scope}"] .logo{
    width:34px;height:34px;border-radius:12px;background:#0f2538;color:#fff;
    display:flex;align-items:center;justify-content:center;font-size:18px;
    box-shadow:0 6px 18px rgba(0,0,0,.08);
  }
  [data-hdr="${scope}"] .brand-name{font-weight:700;color:#0f1720}

  /* —— 关键：单条横线的导航 —— */
[data-hdr="${scope}"] .nav{
  display:flex; align-items:center; height:64px;
  gap:12px; margin-left:8px;
  flex:0 0 auto;          /* ✅ 只占自身需要的宽度，不被拉伸 */
  white-space: nowrap;    /* ✅ 不换行 */
}
[data-hdr="${scope}"] .link{
  display:inline-flex; align-items:center; gap:6px; height:64px;
  padding:0 6px;                /* ✅ 从 8px 改成 6px */
  font-weight:600; color:#334155; text-decoration:none;
  border-bottom:2px solid transparent;
  transition:color .15s ease, border-color .15s ease;
  white-space:nowrap;
}
  [data-hdr="${scope}"] .link i{font-size:15px}
  [data-hdr="${scope}"] .link:hover{color:#0f2538}
  [data-hdr="${scope}"] .link.active{color:#0f2538; border-bottom-color:#0f2538;}  /* 底部横线高亮 */

  /* 搜索框 */
[data-hdr="${scope}"] .search-wrap{
  position:relative;
  flex:1 1 0;                   /* ✅ 可以被压缩 */
  min-width:0;                  /* ✅ 必须有，允许真正缩小 */
  max-width:clamp(280px, 30vw, 680px);  /* ✅ 小屏时自动更窄 */
}
[data-hdr="${scope}"] .search{
  width:100%; height:40px; border:1px solid #e6eaf0; border-radius:12px;
  padding:0 12px 0 36px; font-size:14px; background:#f9fbfc;
}
  [data-hdr="${scope}"] .search-icon{
    position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#8aa0b3; font-size:16px;
  }

  /* 右侧区域 */
[data-hdr="${scope}"] .right{
  display:flex; align-items:center; gap:10px; margin-left:8px;
  flex:0 0 auto;                /* ✅ 固定一块 */
}
  [data-hdr="${scope}"] .city{
    display:inline-flex; align-items:center; gap:6px;
    min-height:40px; padding:8px 12px; background:#f6f8fb; border:1px solid #e7edf5;
    border-radius:12px; color:#0f1720; cursor:pointer;
  }
  [data-hdr="${scope}"] .icon-btn{
    width:36px;height:36px;border-radius:50%;border:1px solid #e7edf5;background:#fff;
    display:flex;align-items:center;justify-content:center; position:relative;
  }
  [data-hdr="${scope}"] .badge{
    position:absolute; right:-2px; top:-2px; min-width:18px; height:18px; padding:0 4px;
    border-radius:999px; background:#e11d48; color:#fff; font-size:11px; display:flex; align-items:center; justify-content:center;
    border:2px solid #fff;
  }
  [data-hdr="${scope}"] .avatar{
    width:36px;height:36px;border-radius:50%;background:#f0f2f6;border:1px solid #e7edf5;
    display:flex;align-items:center;justify-content:center;font-weight:700;color:#0f1720;
  }
    @media (max-width: 1360px){
  [data-hdr="${scope}"] .search-wrap{ max-width:520px; }
}
@media (max-width: 1200px){
  [data-hdr="${scope}"] .search-wrap{ max-width:420px; }
}
@media (max-width: 1100px){
  [data-hdr="${scope}"] .search-wrap{ flex-basis:280px; }
}

/* ===== Desktop Header (scoped) ===== */
[data-hdr] .bar{
  position:sticky; top:0; z-index:100;
  background:#fff; border-bottom:1px solid #eef2f6;
}

[data-hdr] .inner{
  max-width:1280px; margin:0 auto;
  display:flex; align-items:center; gap:12px;
  padding:10px 16px;
}

/* 品牌 */
[data-hdr] .brand{
  display:flex; align-items:center; gap:10px;
  padding:6px 8px; border-radius:12px; cursor:pointer;
  transition:transform .18s ease, background .18s ease;
}
[data-hdr] .brand:hover{ background:#f3f6fb; transform:translateY(-1px); }
[data-hdr] .brand-name{ font-weight:800; color:#0f2538; }

/* 单行下划线导航（.nav 的直接子元素都当作 Tab） */
[data-hdr] .nav{
  display:flex; align-items:center; gap:8px; flex:0 0 auto;
}
[data-hdr] .nav > *{
  position:relative; display:inline-flex; align-items:center; gap:8px;
  height:40px; padding:0 14px; border-radius:12px;
  background:transparent; color:#0f2538; font-weight:700;
  border:1px solid transparent; cursor:pointer;
  transition:background .18s ease, color .18s ease,
             border-color .18s ease, transform .18s ease;
}
[data-hdr] .nav > * i{ font-size:16px; }

/* 悬浮/激活 */
[data-hdr] .nav > *:hover{
  background:#0f2538; color:#fff; border-color:#0f2538;
  transform:translateY(-1px);
}
[data-hdr] .nav > *[data-active="true"],
[data-hdr] .nav > *.is-active{
  background:#0f2538; color:#fff; border-color:#0f2538;
}
/* 底部指示条 */
[data-hdr] .nav > *::after{
  content:""; position:absolute; left:12px; right:12px; bottom:-8px;
  height:2px; background:#0f2538; border-radius:2px;
  transform:scaleX(0); transform-origin:center; transition:transform .18s ease;
}
[data-hdr] .nav > *:hover::after,
[data-hdr] .nav > *[data-active="true"]::after,
[data-hdr] .nav > *.is-active::after{ transform:scaleX(1); }

/* 搜索 */
[data-hdr] .search-wrap{ position:relative; flex:1 1 420px; min-width:280px; }
[data-hdr] .search-wrap .search-icon{
  position:absolute; left:12px; top:50%; transform:translateY(-50%);
  color:#8aa0b3; pointer-events:none;
}
[data-hdr] .search-wrap .search{
  width:100%; height:40px; padding:0 12px 0 36px;
  border:1px solid #e6eaf0; border-radius:12px; background:#f9fbfc;
  transition:border-color .18s ease, box-shadow .18s ease;
}
[data-hdr] .search-wrap .search:hover{ border-color:#cfd9e4; }
[data-hdr] .search-wrap .search:focus{
  outline:none; border-color:#2a61ff; box-shadow:0 0 0 3px rgba(42,97,255,.12);
}

/* 右侧区域 */
[data-hdr] .right{ display:flex; align-items:center; gap:10px; margin-left:8px; }

/* 城市 pill */
[data-hdr] .city{
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 10px; border-radius:12px;
  background:#f4f7fb; border:1px solid #e7edf5; color:#0f2538;
  cursor:pointer; transition:background .18s ease, border-color .18s ease, transform .18s ease;
}
[data-hdr] .city:hover{ background:#eef4ff; border-color:#dbe6ff; transform:translateY(-1px); }

/* 右侧图标按钮（通知等） */
[data-hdr] .icon-btn{
  position:relative;
  width:40px; height:40px; border-radius:12px;
  display:inline-flex; align-items:center; justify-content:center;
  border:1px solid #e7edf5; background:#fff; color:#0f2538;
  transition:background .18s ease, color .18s ease, border-color .18s ease, transform .18s ease;
}
[data-hdr] .icon-btn:hover{
  background:#0f2538; color:#fff; border-color:#0f2538; transform:translateY(-1px);
}

/* 未读徽标 */
[data-hdr] .icon-btn .badge{
  position:absolute; top:-4px; right:-4px;
  min-width:18px; height:18px; padding:0 4px;
  border-radius:999px; background:#ef4444; color:#fff;
  font-size:12px; line-height:18px; text-align:center; font-weight:800;
  box-shadow:0 0 0 2px #fff;
}

/* 头像 */
[data-hdr] .avatar{
  width:32px; height:32px; border-radius:50%;
  background:#0f2538; color:#fff; font-weight:800;
  display:flex; align-items:center; justify-content:center;
}

[data-hdr] .avatar-link{
  display:inline-flex; align-items:center; gap:6px;
  text-decoration:none; color:inherit; cursor:pointer;
}
[data-hdr] .avatar{ width:32px; height:32px; border-radius:50%; overflow:hidden;
  background:#e9eef5; border:1px solid #e7edf5; display:flex; align-items:center; justify-content:center; }
[data-hdr] .avatar img{ width:100%; height:100%; object-fit:cover; display:block; }


/* 减少动画时尊重系统设置 */
@media (prefers-reduced-motion: reduce){
  [data-hdr] .brand,
  [data-hdr] .nav > *,
  [data-hdr] .icon-btn,
  [data-hdr] .city{ transition:none }
  [data-hdr] .nav > *::after{ transition:none }
}
  `;

  const doSearch = (e) => {
    if (e.key === "Enter" && onSearch) onSearch(e.target.value);
  };

  const Link = ({ to, icon, label, isActive }) => (
    <a className={`link ${isActive ? "active" : ""}`} onClick={() => nav(to)}>
      <i className={`bi ${icon}`} /> {label}
    </a>
  );

  return (
    <>
      <style>{css}</style>
      <header className="bar" data-hdr={scope}>
        <div className="inner">
          {/* 品牌 */}
          <div className="brand" onClick={() => nav("/")}>
            <div className="logo"><img src="/assets/logocity.jpg" alt="CityGetAway" style={{ width: 40, height: 40 }} /></div>
            <div className="brand-name">CityGetAway</div>
          </div>

          {/* 单行下划线导航 */}
          <nav className="nav">
            <Link to="/features" icon="bi-house-door" label="Home" isActive={active === "home"} />
            <Link to="/trip-planner" icon="bi-columns-gap" label="Trip Planner" isActive={active === "planner"} />
            <Link to="/community" icon="bi-people" label="Community" isActive={active === "community"} />
            <Link to="/message" icon="bi-chat-dots" label="Messages" isActive={active === "messages"} />
          </nav>

          {/* 搜索 */}
          <div className="search-wrap">
            <i className="bi bi-search search-icon" />
            <input
              className="search"
              type="search"
              placeholder="Search destinations, activities, restaurants…"
              onKeyDown={doSearch}
            />
          </div>

          {/* 右侧 */}
          <div className="right">
            <div className="city">
              <i className="bi bi-geo-alt" />
              {cityName}
              <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} />
            </div>

            <button className="icon-btn" aria-label="Notifications" onClick={() => setOpenNtf(true)}>
              <i className="bi bi-bell" />
              {unread > 0 && <span className="badge">{unread}</span>}
            </button>

            {/* 通知面板 */}
            <NotifacitonPanel
              open={openNtf}
              onClose={() => setOpenNtf(false)}
              onMarkAllRead={() => {/* 把 items 标记已读的逻辑放这里 */ }}
              items={items}
              // 可选：自定义定位
              anchorRight={16}
              anchorTop={56}
            />


            <NavLink to="/profile" className="avatar-link" araia-label="Profile">

              <div className="avatar">

                <img src="https://i.pinimg.com/1200x/fd/20/13/fd20135e219e1336b7fdc478dca13d90.jpg" alt="User avatar">
                </img>
              </div>
              <i className="bi bi-caret-down-fill" style={{ fontSize: 10, marginLeft: 4 }} />
            </NavLink>

          </div>
        </div>
      </header>
    </>
  );
}
